import { z } from 'zod';

/**
 * Validation for an institute's operating settings. Mirrors the backend's
 * `updateInstituteSettingsSchema` — the server is still the authority (CLAUDE.md
 * §10); these rules are here so a bad time or prefix is caught in the field
 * rather than after a round trip.
 *
 * Bank, payment-gateway and SMS credentials are stored on the same row but are
 * deliberately not part of this form: they are secrets, and the API neither
 * returns them nor accepts them here.
 */

const clockTime = z
  .string()
  .trim()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Use a 24-hour time such as 08:30');

/** Prefixes end up in admission numbers and receipt numbers: SPS/2026/0142. */
const numberingPrefix = (max: number) =>
  z
    .string()
    .trim()
    .toUpperCase()
    .min(1, 'A prefix is required')
    .max(max, `At most ${max} characters`)
    .regex(/^[A-Z0-9/-]+$/, 'Letters, numbers, / and - only');

export const settingsSchema = z
  .object({
    dateFormat: z.enum(['DD/MM/YYYY', 'DD-MM-YYYY', 'YYYY-MM-DD']),

    weekOffDays: z
      .array(
        z.enum(['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY']),
      )
      .max(6, 'An institute cannot be closed every day'),
    workingDaysPerWeek: z.coerce.number().int().min(1, 'At least 1').max(7, 'At most 7'),
    schoolStartTime: clockTime,
    schoolEndTime: clockTime,
    periodsPerDay: z.coerce.number().int().min(1, 'At least 1').max(20, 'At most 20'),
    periodDurationMinutes: z.coerce
      .number()
      .int()
      .min(10, 'At least 10 minutes')
      .max(120, 'At most 120 minutes'),
    attendanceMode: z.enum(['DAILY', 'PERIOD_WISE']),

    gradingSystem: z.enum(['MARKS', 'GRADE', 'CGPA', 'CCE']),
    passingPercentage: z.coerce.number().int().min(0, 'Cannot be negative').max(100, 'At most 100'),

    admissionNumberPrefix: numberingPrefix(12),
    admissionNumberNext: z.coerce.number().int().min(1, 'At least 1').max(999_999),
    receiptPrefix: numberingPrefix(12),
    receiptNumberNext: z.coerce.number().int().min(1, 'At least 1').max(999_999),

    notifyParentsOnAbsence: z.boolean(),
    notifyParentsOnFeeDue: z.boolean(),
    notifyParentsOnResult: z.boolean(),
  })
  .refine((data) => data.schoolEndTime > data.schoolStartTime, {
    path: ['schoolEndTime'],
    message: 'The institute day must end after it starts',
  })
  .refine((data) => data.workingDaysPerWeek + data.weekOffDays.length <= 7, {
    path: ['workingDaysPerWeek'],
    message: 'Working days and week offs cannot add up to more than seven',
  });

export type SettingsFormValues = z.input<typeof settingsSchema>;
export type SettingsPayload = z.output<typeof settingsSchema>;
